'use client';
import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import { Award, Users, Globe, Film, Star, Trophy, Target, Heart } from 'lucide-react';

const stats = [
{ value: '3', label: 'Seasons', icon: <Trophy size={20} /> },
{ value: '2,400+', label: 'Entries Received', icon: <Film size={20} /> },
{ value: '18', label: 'Award Categories', icon: <Award size={20} /> },
{ value: '50+', label: 'Expert Judges', icon: <Star size={20} /> },
{ value: '₹5L+', label: 'Total Prize Pool', icon: <Target size={20} /> },
{ value: '12+', label: 'Cities Represented', icon: <Globe size={20} /> }];


const team = [
{
  id: 'team-1',
  name: 'Karthik Sundaram',
  role: 'Founder & Director',
  bio: 'Award-winning filmmaker with 15 years in Tamil cinema. Passionate about nurturing new voices.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e5d3897-1774339311307.png",
  alt: 'Karthik Sundaram, Founder and Director of Motion Max Media Awards'
},
{
  id: 'team-2',
  name: 'Divya Krishnan',
  role: 'Creative Director',
  bio: 'Documentary filmmaker and visual storyteller. Curates the judging criteria and category standards.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16f78ea2d-1781587091404.png",
  alt: 'Divya Krishnan, Creative Director of Motion Max Media Awards'
},
{
  id: 'team-3',
  name: 'Rajan Pillai',
  role: 'Head of Operations',
  bio: 'Event management veteran with experience running large-scale media festivals across South India.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14a5ca983-1763300171126.png",
  alt: 'Rajan Pillai, Head of Operations at Motion Max Media Awards'
},
{
  id: 'team-4',
  name: 'Ananya Bose',
  role: 'Partnerships Lead',
  bio: 'Builds relationships with sponsors, media partners, and industry organizations to amplify creator reach.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1206516c0-1772752363755.png",
  alt: 'Ananya Bose, Partnerships Lead at Motion Max Media Awards'
}];


const values = [
{ icon: <Film size={24} />, title: 'Authentic Storytelling', desc: 'We believe every story deserves to be told. We champion raw, authentic voices over polished but hollow productions.' },
{ icon: <Users size={24} />, title: 'Community First', desc: 'Motion Max is built by creators, for creators. Every decision we make centers the needs of our filmmaking community.' },
{ icon: <Globe size={24} />, title: 'Regional Diversity', desc: 'From Tamil Nadu to the Northeast, we celebrate the rich tapestry of Indian regional storytelling traditions.' },
{ icon: <Heart size={24} />, title: 'Inclusive Excellence', desc: 'Great storytelling has no age, budget, or equipment barrier. We judge ideas, not production value alone.' }];


export default function AboutPage() {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section ref={heroRef} className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-16">
        <div
          className="absolute inset-0 will-change-transform"
          style={{ transform: `translateY(${scrollY * 0.3}px) scale(1.1)` }}>
          
          <Image
            src="https://img.rocket.new/generatedImages/rocket_gen_img_14ad861be-1767306026898.png"
            alt="Film production crew working on a cinematic set with cameras and lighting"
            fill
            priority
            className="object-cover"
            sizes="100vw" />
          
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-background" />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.1) 0%, transparent 70%)' }} />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)' }}>
            <Award size={14} className="text-primary" />
            <span className="text-xs font-semibold text-primary tracking-widest uppercase">Our Story</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-extrabold text-white mb-6" style={{ letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            Born from a <span className="gradient-gold-text">Passion</span><br />for Cinema
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Motion Max Media Awards was founded to give India&apos;s independent filmmakers, content creators, and digital storytellers the recognition they deserve.
          </p>
        </div>
      </section>

      {/* Stats bento */}
      <section className="py-20 px-6 lg:px-10 max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {stats?.map((stat) =>
          <div key={`stat-${stat?.label}`} className="glass-card rounded-2xl p-5 text-center group hover:border-primary/40 transition-all">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                {stat?.icon}
              </div>
              <p className="text-2xl font-extrabold gradient-gold-text mb-1">{stat?.value}</p>
              <p className="text-xs text-muted-foreground font-medium">{stat?.label}</p>
            </div>
          )}
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-6 lg:px-10 max-w-screen-2xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">Our Mission</p>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-foreground mb-6" style={{ letterSpacing: '-0.025em', lineHeight: 1.1 }}>
              Elevating India&apos;s <span className="gradient-gold-text">Creative Voice</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-5 text-lg">
              We started Motion Max in 2023 with a simple belief: India has an extraordinary depth of storytelling talent that goes unrecognized because the gatekeepers are too few and too far away.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Our platform is designed to be the bridge — connecting passionate creators with industry professionals, sponsors, and audiences who are hungry for authentic Indian stories. Every submission we receive is a story waiting to be heard.
            </p>
            <Link href="/register" className="btn-primary px-8 py-4 rounded-full font-bold inline-flex items-center gap-2">
              <Award size={18} /> Join Season 3
            </Link>
          </div>
          <div className="relative">
            <div className="relative h-[500px] rounded-3xl overflow-hidden">
              <Image
                src="https://img.rocket.new/generatedImages/rocket_gen_img_19e972de1-1773075005289.png"
                alt="Film director reviewing footage on a cinema monitor during production"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-5 max-w-xs" style={{ background: 'rgba(17,17,17,0.95)', border: '1px solid rgba(201,168,76,0.3)' }}>
              <p className="text-sm font-bold text-foreground mb-1">Season 3 — 2026</p>
              <p className="text-xs text-muted-foreground">Submissions open until 31 August 2026. ₹5 Lakh+ in prizes across 18 categories.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-6 lg:px-10 max-w-screen-2xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">What We Stand For</p>
          <h2 className="text-4xl font-extrabold text-foreground" style={{ letterSpacing: '-0.025em' }}>
            Our <span className="gradient-gold-text">Core Values</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {values?.map((v, i) =>
          <div key={`value-${i}`} className="glass-card glass-card-hover rounded-2xl p-7">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-5">
                {v?.icon}
              </div>
              <h3 className="font-bold text-foreground mb-3">{v?.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{v?.desc}</p>
            </div>
          )}
        </div>
      </section>

      {/* Team */}
      <section className="py-20 px-6 lg:px-10 max-w-screen-2xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">The People Behind It</p>
          <h2 className="text-4xl font-extrabold text-foreground" style={{ letterSpacing: '-0.025em' }}>
            Meet the <span className="gradient-gold-text">Team</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {team?.map((member) =>
          <div key={member?.id} className="glass-card glass-card-hover rounded-2xl overflow-hidden group">
              <div className="relative h-56 overflow-hidden">
                <Image
                src={member?.avatar}
                alt={member?.alt}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              </div>
              <div className="p-5">
                <h3 className="font-bold text-foreground mb-0.5">{member?.name}</h3>
                <p className="text-xs font-semibold text-primary mb-3">{member?.role}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{member?.bio}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center glass-card rounded-3xl p-12" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <Trophy size={40} className="text-primary mx-auto mb-5" />
          <h2 className="text-3xl font-extrabold text-foreground mb-4" style={{ letterSpacing: '-0.025em' }}>
            Ready to Share Your Story?
          </h2>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            Join thousands of creators who have already submitted their work. Season 3 submissions are open now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register" className="btn-primary px-8 py-4 rounded-full font-bold inline-flex items-center gap-2 justify-center">
              <Award size={18} /> Submit Your Entry
            </Link>
            <Link href="/categories" className="btn-outline-gold px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 justify-center">
              View Categories
            </Link>
          </div>
        </div>
      </section>

      <HomeFooter />
    </div>);

}